import { useCallback } from 'react'
import { CurrentLocationState } from 'shop/components'
import { LandingState } from 'shop/components/Landing/LandingContext'
import {
  GeocodedAddress,
  isAddressSameAsCurrentLocation,
  handleAddressSelect
} from 'shop/components/Landing/LocationSelect/utils'
import { trackPostCodeChange } from 'shop/components/Landing/utils'
import { CustomerAddress } from 'shop/types'
import { useSessionStorage } from '../useSessionStorage'

type Props = {
  setUseCustomerAddress: LandingState['setUseCustomerAddress']
  currentLocation: LandingState['currentLocation']
  setCurrentLocation: LandingState['setCurrentLocation']
  setMapInteracts: LandingState['setMapInteracts']
}

export const useLandingLocation = ({
  setUseCustomerAddress,
  currentLocation,
  setCurrentLocation,
  setMapInteracts
}: Props) => {
  const {
    setSessionStorageItem: setSavedCurrentLocation,
    removeSessionStorageItem: removeSavedCurrentLocation
  } = useSessionStorage<CurrentLocationState>('saved-current-location')

  const handleLocationChange = (
    newAddress: string,
    options?: { clearLocationOnChange?: boolean }
  ) => {
    setMapInteracts({ currentlyViewedStore: null, currentlyHoveredStore: null })
    setUseCustomerAddress(false)
    // Reset currentLocation when newAddress is empty. Used for "Clear" control.
    // also resets when we don't want to persist the currentLocation between user input changes.
    if (!newAddress || !!options?.clearLocationOnChange)
      setCurrentLocation(undefined)
  }

  const handleLocationSelect = useCallback(
    ({
      newAddress,
      geocodedAddress
    }: {
      newAddress?: string
      geocodedAddress?: GeocodedAddress
    }) => {
      setMapInteracts({
        currentlyViewedStore: null,
        currentlyHoveredStore: null
      })
      if (!newAddress && !geocodedAddress) {
        setCurrentLocation(undefined)
        removeSavedCurrentLocation()
        return
      }
      // if we are selecting the same address, skip geocode.
      if (
        isAddressSameAsCurrentLocation(currentLocation, newAddress) ||
        isAddressSameAsCurrentLocation(
          currentLocation,
          geocodedAddress?.formattedAddress
        )
      )
        return

      handleAddressSelect({
        singleLineAddress: newAddress,
        geocodedAddress
      }).then((selectedAddress) => {
        if (!selectedAddress) return
        const { formattedAddress, addressComponents, latLng } =
          selectedAddress as GeocodedAddress
        const locationValues: CurrentLocationState = {
          address: formattedAddress,
          addressComponents: addressComponents,
          latLng: latLng
        }
        setCurrentLocation(locationValues)
        setSavedCurrentLocation(locationValues)
        trackPostCodeChange('landing')
      })
    },
    [setCurrentLocation, currentLocation]
  )

  /** Handles the location select in the case of a Customer's Address */
  const selectCustomerAddress = (customerAddress: CustomerAddress) => {
    setMapInteracts({ currentlyViewedStore: null, currentlyHoveredStore: null })
    handleAddressSelect({ customerAddress }).then((selectedAddress) => {
      if (!selectedAddress) return
      const { address, addressId, addressComponents, latLng } =
        selectedAddress as CurrentLocationState
      // even though we are relying on `currentLocation.addressId` on the Landing page.
      // continue to set the `useCustomerAddress` state for the LandingContext.
      setUseCustomerAddress(true)
      const locationValues: CurrentLocationState = {
        address: address,
        addressId: addressId,
        addressComponents: addressComponents,
        latLng: latLng
      }
      setCurrentLocation(locationValues)
      setSavedCurrentLocation(locationValues)
      trackPostCodeChange('landing')
    })
  }

  return {
    handleLocationChange,
    handleLocationSelect,
    selectCustomerAddress
  }
}
